import { AlertTriangleIcon, RefreshCcwIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { devMode } from '@core/utils/misc';

import { Button } from './Button';
import { Card, CardContent } from './Card';
import { Text } from './Text';

interface ErrorFallbackProps {
  error?: Error;
  onReset: () => void;
}

/**
 * A fallback component displayed when an error occurs in the application.
 *
 * Features:
 * - Displays a user-friendly error message
 * - Shows detailed error information in development mode
 * - Provides a reset mechanism to recover from errors
 * - Responsive design with proper error handling
 *
 * @example
 * ```tsx
 * <ErrorBoundary fallback={ErrorFallback}>
 *   <YourComponent />
 * </ErrorBoundary>
 * ```
 */
export default function ErrorFallback({ error, onReset }: ErrorFallbackProps) {
  const { t } = useTranslation();

  return (
    <main className="flex min-h-screen w-full items-center justify-center">
      <Card className="max-w-md">
        <div className="flex items-center gap-4 px-6 pt-4">
          <div className="rounded-full bg-red-500 p-3">
            <AlertTriangleIcon className="size-8" />
          </div>
          <Text as="h1" size="3xl" weight="medium">
            {t('errors.somethingWentWrong')}
          </Text>
        </div>

        <CardContent>
          <Text size="sm">{t('errors.unexpectedError')}</Text>

          {devMode && error && (
            <pre className="bg-background-light dark:bg-background-dark mt-4 max-h-48 overflow-auto rounded-lg p-4 text-sm">
              <code>{error.message}</code>
              <code>{error.stack}</code>
            </pre>
          )}

          <Button onClick={onReset} className="mt-8 w-full justify-center gap-2">
            <RefreshCcwIcon className="size-4" />
            {t('common.tryAgain')}
          </Button>
        </CardContent>
      </Card>
    </main>
  );
}
